/**
 * Data Mode — localStorage-backed setting for data source selection.
 *
 * 'smart-relay'  — Default: relay caches all public data server-side, streams to dashboard
 * 'proxy'        — Fallback: dashboard makes individual REST/WS calls via relay proxy (uses user key for all data)
 */

import { storageKey } from '@/lib/infra/storage';

export type DataMode = 'proxy' | 'smart-relay';

const STORAGE_KEY = storageKey('data_mode');
const VERSION_KEY = storageKey('data_mode_v');
const CURRENT_VERSION = '2';
let currentMode: DataMode = 'proxy';

/** Initialize from localStorage. Defaults to proxy. Smart-relay available but requires relay-side config.
 *  Auto-fallback in useStreamManagement ensures smart-relay degrades gracefully to proxy. */
export function initDataMode(): DataMode {
  const version = localStorage.getItem(VERSION_KEY);
  if (version !== CURRENT_VERSION) {
    localStorage.setItem(STORAGE_KEY, 'proxy');
    localStorage.setItem(VERSION_KEY, CURRENT_VERSION);
    currentMode = 'proxy';
    return currentMode;
  }

  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved === 'proxy' || saved === 'smart-relay') {
    currentMode = saved;
  }
  return currentMode;
}

/** Get current data mode. */
export function getDataMode(): DataMode {
  return currentMode;
}

/** Set data mode and persist to localStorage. */
export function setDataMode(mode: DataMode): void {
  currentMode = mode;
  localStorage.setItem(STORAGE_KEY, mode);
}

/** Toggle between proxy and smart-relay. */
export function toggleDataMode(): DataMode {
  const next = currentMode === 'proxy' ? 'smart-relay' : 'proxy';
  setDataMode(next);
  return next;
}
